import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
	ExpansionPanel,
	ExpansionPanelDetails,
	ExpansionPanelSummary,
	Typography,
	Button,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AddIcon from "@material-ui/icons/Add";
import { useRouter, useTerminology } from "utils";
import CustomerSites from "./sites";

const useStyles = makeStyles((theme) => ({
	root: {
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.white,
		overflow: "hidden",
		boxShadow: "none",
		"&:before": {
			display: "none",
		},
	},
	summary: {
		padding: theme.spacing(0, 1.5, 0, 2),
		minHeight: 52,
		"&.Mui-expanded": {
			minHeight: 52,
		},
		"& .MuiExpansionPanelSummary-content": {
			margin: theme.spacing(1.25, 0),
			alignItems: "center",
		},
	},
	summaryTitle: {
		fontWeight: 600,
		fontSize: "1rem",
		color: theme.palette.text.primary,
	},
	countMuted: {
		fontWeight: 500,
		color: theme.palette.text.secondary,
		marginLeft: theme.spacing(0.5),
	},
	expandIcon: {
		color: theme.palette.text.secondary,
	},
	details: {
		padding: 0,
		display: "block",
		overflow: "hidden",
	},
	addRow: {
		display: "flex",
		justifyContent: "flex-start",
		padding: theme.spacing(1.5, 2, 0, 2),
	},
	addButton: {
		color: theme.palette.text.secondary,
		fontWeight: 600,
		"&:hover": {
			color: theme.palette.brand,
		},
	},
	addIcon: {
		marginRight: theme.spacing(1),
	},
	embeddedRoot: {
		maxHeight: "min(70vh, 720px)",
		overflowY: "auto",
	},
}));

const CustomerSitesPanel = ({
	customer,
	expanded,
	onExpandedChange,
	onAddSite,
	siteList,
	role,
	onEdit,
	onDelete,
	onSelectSite,
}) => {
	const classes = useStyles();
	const { match } = useRouter();
	const { sitesLabel } = useTerminology();
	const customerIdFromRoute = match?.params?.customerId;
	const customerId = customer?.id || customerIdFromRoute;

	const sitesCount = useMemo(() => {
		if (!customerId || !siteList) {
			return null;
		}
		return siteList.filter((s) => s.customerId === customerId).length;
	}, [customerId, siteList]);

	if (!customer || !customerId) {
		return null;
	}

	const countLabel =
		sitesCount !== null && sitesCount !== undefined
			? ` (${sitesCount})`
			: "";

	return (
		<ExpansionPanel
			className={classes.root}
			elevation={0}
			expanded={expanded}
			onChange={onExpandedChange}
			data-cy="customer-sites-panel"
		>
			<ExpansionPanelSummary
				expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
				className={classes.summary}
				aria-controls="customer-sites-panel-content"
				id="customer-sites-panel-header"
			>
				<Typography component="h2" className={classes.summaryTitle}>
					{sitesLabel}
					{countLabel !== "" && (
						<span className={classes.countMuted}>{countLabel}</span>
					)}
				</Typography>
			</ExpansionPanelSummary>
			<ExpansionPanelDetails className={classes.details}>
				{expanded && onAddSite && (
					<div className={classes.addRow}>
						<Button
							className={classes.addButton}
							variant="contained"
							onClick={onAddSite}
							data-cy="customer-add-site"
						>
							<AddIcon className={classes.addIcon} />
							Add Site
						</Button>
					</div>
				)}
				{expanded && (
					<div className={classes.embeddedRoot}>
						<CustomerSites
							variant="embedded"
							customerId={customerId}
							siteList={siteList}
							role={role}
							onEdit={onEdit}
							onDelete={onDelete}
							onSelectSite={onSelectSite}
						/>
					</div>
				)}
			</ExpansionPanelDetails>
		</ExpansionPanel>
	);
};

CustomerSitesPanel.propTypes = {
	customer: PropTypes.object,
	expanded: PropTypes.bool.isRequired,
	onExpandedChange: PropTypes.func.isRequired,
	onAddSite: PropTypes.func,
	siteList: PropTypes.array,
	role: PropTypes.string,
	onEdit: PropTypes.func,
	onDelete: PropTypes.func,
	onSelectSite: PropTypes.func,
};

export default CustomerSitesPanel;
